import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Unified Pagination Component
 * 
 * Supports two modes:
 * 1. Stateless Mode: Parent manages state, component just triggers page changes
 *    - Props: activePage, itemsCountPerPage, totalItemsCount, onChange
 * 2. Stateful Mode: Component manages data slicing internally
 *    - Props: items, onChangePage, initialPage, pageSize
 * 
 * Uses shadcn/Tailwind with brand colors (#313131, #f19f57, #f9f9f9)
 */
class Pagination extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      pager: {},
      currentPage: props.initialPage || 1
    };
  }

  componentDidMount() {
    // Stateful mode: set page if items array isn't empty
    if (this.props.items && this.props.items.length) {
      this.setPage(this.props.initialPage || 1);
    }
  }

  componentDidUpdate(prevProps) {
    // Stateful mode: reset page if items array has changed
    if (this.props.items && this.props.items !== prevProps.items) {
      this.setPage(this.props.initialPage || 1);
    }
  }

  setPage(page) {
    const { items, pageSize, onChangePage } = this.props;
    
    if (!items) return; // Not in stateful mode

    let pager = this.state.pager;

    if (page < 1 || (pager.totalPages && page > pager.totalPages)) {
      return;
    }

    // Get new pager object for specified page
    pager = this.getPager(items.length, page, pageSize || 10);

    // Get new page of items from items array
    const pageOfItems = items.slice(pager.startIndex, pager.endIndex + 1);

    // Update state
    this.setState({ pager, currentPage: page });

    // Call change page function in parent component
    if (onChangePage) {
      onChangePage(pageOfItems);
    }
  }

  getPager(totalItems, currentPage, pageSize) {
    currentPage = currentPage || 1;
    pageSize = pageSize || 10;

    const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;

    let startPage, endPage;
    if (totalPages <= 10) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 6) {
        startPage = 1;
        endPage = 10;
      } else if (currentPage + 4 >= totalPages) {
        startPage = totalPages - 9;
        endPage = totalPages;
      } else {
        startPage = currentPage - 5;
        endPage = currentPage + 4;
      }
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);
    const pages = [...Array((endPage + 1) - startPage).keys()].map(i => startPage + i);

    return {
      totalItems,
      currentPage,
      pageSize,
      totalPages,
      startPage,
      endPage,
      startIndex,
      endIndex,
      pages
    };
  }

  getPageNumbers() {
    const { pageRangeDisplayed = 5, activePage, itemsCountPerPage, totalItemsCount } = this.props;
    
    const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
    const pages = [];
    const halfRange = Math.floor(pageRangeDisplayed / 2);
    let startPage = Math.max(1, activePage - halfRange);
    let endPage = Math.min(totalPages, activePage + halfRange);

    if (activePage <= halfRange) {
      endPage = Math.min(pageRangeDisplayed, totalPages);
    }

    if (activePage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - pageRangeDisplayed + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  handlePageChange = (page) => {
    const { activePage, onChange, itemsCountPerPage, totalItemsCount } = this.props;
    const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
    
    if (page >= 1 && page <= totalPages && page !== activePage) {
      onChange(page);
    }
  }

  render() {
    const {
      // Stateless mode props
      activePage,
      itemsCountPerPage,
      totalItemsCount,
      onChange,
      pageRangeDisplayed = 5,
      
      // Stateful mode props
      items,
      
      // Shared props
      hideFirstLastPages = false,
      showInfo = true,
      className = "",
      innerClass = "",
      itemClass = "",
      linkClass = "",
      activeClass = "",
      activeLinkClass = ""
    } = this.props;

    // Determine which mode we're in
    const isStatefulMode = !!items;
    const isStatelessMode = !items && typeof activePage !== 'undefined';

    let pager, currentPage, totalPages, startIndex, endIndex, totalItems, pages;

    if (isStatefulMode) {
      // Stateful mode: use internal state
      pager = this.state.pager;
      if (!pager.pages || pager.pages.length <= 1) {
        return null;
      }
      currentPage = pager.currentPage;
      totalPages = pager.totalPages;
      startIndex = pager.startIndex;
      endIndex = pager.endIndex;
      totalItems = pager.totalItems;
      pages = pager.pages;
    } else if (isStatelessMode) {
      // Stateless mode: calculate from props
      totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
      if (totalPages <= 1) return null;
      
      currentPage = activePage;
      startIndex = (activePage - 1) * itemsCountPerPage;
      endIndex = Math.min(startIndex + itemsCountPerPage - 1, totalItemsCount - 1);
      totalItems = totalItemsCount;
      pages = this.getPageNumbers();
    } else {
      return null;
    }

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    const handleClick = (page) => {
      if (isStatefulMode) {
        this.setPage(page);
      } else {
        this.handlePageChange(page);
      }
    };

    return (
      <div className={cn("flex flex-col sm:flex-row items-center justify-between px-4 py-3 gap-3 border-t bg-white", className)}>
        {/* Info Section */}
        {showInfo && (
          <div className="text-sm text-brand-dark">
            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
            <span className="font-medium">{endIndex + 1}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </div>
        )}

        {/* Pagination Controls */}
        <div className={cn("flex items-center gap-1", innerClass)}>
          {/* First Page */}
          {!hideFirstLastPages && (
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "h-8 w-8 text-brand-dark hover:bg-brand-dark hover:text-white",
                itemClass,
                isFirstPage && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => handleClick(1)}
              disabled={isFirstPage}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Previous Page */}
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "h-8 w-8 text-brand-dark hover:bg-brand-dark hover:text-white",
              itemClass,
              isFirstPage && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => handleClick(currentPage - 1)}
            disabled={isFirstPage}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page Numbers */}
          {pages.map((page, index) => (
            <Button
              key={index}
              variant={page === currentPage ? "default" : "outline"}
              size="icon"
              className={cn(
                "h-8 w-8",
                itemClass,
                page === currentPage
                  ? cn("bg-brand-dark text-white hover:bg-brand-dark hover:text-white", activeClass, activeLinkClass)
                  : "text-brand-dark hover:bg-brand-dark hover:text-white",
                linkClass
              )}
              onClick={() => handleClick(page)}
            >
              {page}
            </Button>
          ))}

          {/* Next Page */}
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "h-8 w-8 text-brand-dark hover:bg-brand-dark hover:text-white",
              itemClass,
              isLastPage && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => handleClick(currentPage + 1)}
            disabled={isLastPage}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last Page */}
          {!hideFirstLastPages && (
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "h-8 w-8 text-brand-dark hover:bg-brand-dark hover:text-white",
                itemClass,
                isLastPage && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => handleClick(totalPages)}
              disabled={isLastPage}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }
}

Pagination.propTypes = {
  // Stateless mode
  activePage: PropTypes.number,
  itemsCountPerPage: PropTypes.number,
  totalItemsCount: PropTypes.number,
  onChange: PropTypes.func,
  
  // Stateful mode
  items: PropTypes.array,
  onChangePage: PropTypes.func,
  initialPage: PropTypes.number,
  pageSize: PropTypes.number,
  
  // Shared
  pageRangeDisplayed: PropTypes.number,
  hideFirstLastPages: PropTypes.bool,
  showInfo: PropTypes.bool,
  className: PropTypes.string,
  innerClass: PropTypes.string,
  itemClass: PropTypes.string,
  linkClass: PropTypes.string,
  activeClass: PropTypes.string,
  activeLinkClass: PropTypes.string
};

Pagination.defaultProps = {
  initialPage: 1,
  pageSize: 10,
  pageRangeDisplayed: 5,
  hideFirstLastPages: false,
  showInfo: true
};

export default Pagination;
export { Pagination };
